import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import { CSVLink } from 'react-csv';
import axios from 'axios';
import { UseAuthContext } from '../../context/AuthContext';
//import mockdata from './mock.json'

export default function CreateBulkAddresses({ setTableData, setTextAreaContent, tabName }) {
	const { t } = useTranslation();
	const { token } = UseAuthContext();
	const [accessType, setAccessType] = useState('attachment');

	// CSV file upload state varaibales 
	const inputFileRef = useRef(null);
	const [file, setFile] = useState(null);
	const [validatedData, setValidatedData] = useState([]);
	const [isFileValid, setIsFileValid] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [validatedMessage, setValidatedMessage] = useState('');
	// Define the mandatory columns
	const mandatoryFields = ['streetName', 'buildingNo', 'buildingSuffix', 'postCode',
		'region', 'municipalityName', 'streetCode', 'coordinates']

	// text area state variables
	const [csvInput, setCsvInput] = useState('');
	const [errors, setErrors] = useState([]);

	//Handle file upload
	const handleFileChange = (e) => {
		
		setErrorMessage(''); 
		setValidatedMessage('');

		const uploadedFile = e.target.files[0];

		if (uploadedFile) {
			setValidatedData([]);
			setTableData({ header: [], data: [] });
			setIsFileValid(false);
			setFile(uploadedFile);
		}
		// Reset input AFTER setting the file so that same file can be re-uploaded
		//e.target.value = ""; 
	};
	const clearFile = () => {
		inputFileRef.current.value = "";
		handleReset()
	};

	// Parse the uploaded CSV and validate it
	const handleCSVValidate = (isCreate = false) => {
		setErrorMessage('');
		if (!file) {
			setErrorMessage('Please upload csv file for addresses creation');
			return;
		}
		// Check if the file is a valid CSV (by MIME type or file extension)
		const validMimeTypes = ['text/csv', 'application/vnd.ms-excel'];
		if (!validMimeTypes.includes(file.type) && !file.name.endsWith('.csv')) {
			setErrorMessage('Invalid file format. Please upload a valid CSV file.');
			return;
		}

		// Check if the file is empty
		if (file.size === 0) {
			setErrorMessage('The uploaded file is empty. Please upload a non-empty CSV file.');
			return;
		}
		/* eslint-disable no-useless-escape */
		const fieldFormats = {
			'streetName': {
				pattern: /^[A-Za-z0-9åøæÅØÆé\s\-]+(vej|gade|plads|bro|sti|allé|torv|bakke|stræde)?$/,
				message: 'Invalid Street Name'
			},
			'buildingNo': {
				pattern: /^[A-Za-z0-9\-\/\s\.]+$/,
				message: 'Invalid Building Id'
			},
			'buildingSuffix': {
				pattern: /^[A-Za-z0-9\-\/\.]+$/,
				message: 'Invalid Building Suffix'
			},
			'floorId': {
				pattern: /^[A-Za-z0-9åøæÅØÆé\s\-\/\.]{1,6}(st|etage|th|g|g1|gf)?$/,
				message: 'Invalid Floor Id'
			},
			'suiteId': {
				pattern: /^[A-Za-z0-9\-\/\.]+$/,
				message: 'Invalid Suit Id'
			},
			'region': {
				pattern: /^[A-Za-zÆØÅæøå\s\-]+$/,
				message: 'Invalid Region'
			},
			'districtName': {
				pattern: /^[A-Za-zÆØÅæøåé0-9\s\-\.]+$/,
				message: 'Invalid District Name'
			},
			'municipalityName': {
				pattern: /^[A-Za-zÆØÅæøåé\s\-]+$/,
				message: 'Invalid Municilaity Name'
			},
			'streetCode': {
				pattern: /^[A-Za-z0-9\-]+$/,
				message: 'Invalid Street Code'
			},
			'postCode': {
				pattern: /^[0-9\s-]{3,10}$/,
				message: 'Invalid Postcode'
			},
			'coordinates': {
				pattern: /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/,
				message: 'Invalid Coordinates'
			},
		};
		// Define the expected headers
		const expectedHeaders = [
			'streetName',
			'buildingNo',
			'buildingSuffix',
			'floorId',
			'suiteId',
			'region',
			'districtName',
			'municipalityName',
			'streetCode',
			'postCode',
			'coordinates'
		];
		/* eslint-enable no-useless-escape */
		Papa.parse(file, {
			skipEmptyLines: true,
			complete: async (result) => {
				const rows = result.data;
				const fileHeaders = result.meta.fields;

				// Check for incorrect or missing headers
				const missingHeaders = expectedHeaders.filter(header => !fileHeaders.includes(header));
				if (missingHeaders.length > 0) {
					setErrorMessage("Invalid file format. Expected headers: streetName,buildingNo,buildingSuffix,floorId,suiteId,postCode,districtName,region,municipalityName,streetCode,coordinates");
					return;
				}

				if (rows.length > 50) {
					setErrorMessage('CSV file contains more than 50 records. Please upload a file with 50 or fewer records.');
					return;
				}
				const duplicateKeyFields = [
					'streetName',
					'buildingNo',
					'buildingSuffix',
					'floorId',
					'suiteId',
					'postCode'
				];

				const seen = new Map();

				rows.forEach((row, index) => {
					const key = duplicateKeyFields
						.map(f => (row[f] || '').trim().toLowerCase())
						.join('|');

					if (seen.has(key)) {
						seen.get(key).duplicates.push(index);
					} else {
						seen.set(key, { originalIndex: index, duplicates: [] });
					}
				});

				// Prepare lookup and row number messages
				const duplicateLookup = {};

				for (const { originalIndex, duplicates } of seen.values()) {
					const originalRowNumber = originalIndex + 1;

					if (duplicates.length > 0) {
						// mark original
						duplicateLookup[originalIndex] = {
							isDuplicate: true,
							message: `This record has duplicate entries at rows: ${duplicates
								.map(d => d + 1)
								.join(', ')}`
						};

						// mark each duplicate row
						duplicates.forEach(d => {
							duplicateLookup[d] = {
								isDuplicate: true,
								message: `This record is a duplicate of row ${originalRowNumber}`
							};
						});
					}
				}

				const validatedRows = rows.map((row, rowIndex) => {
					const fieldErrors = {};

					// Mandatory fields
					mandatoryFields.forEach((field) => {
						if (!row[field] || row[field].trim() === '') {
							fieldErrors[field] = 'This field is required';
						}
					});

					// Format validation
					Object.entries(fieldFormats).forEach(([field, { pattern, message }]) => {
						const value = (row[field]).trim();
						if (value && !fieldErrors[field]) {
							if (!pattern.test(value.trim())) {
								fieldErrors[field] = message;
							}
						}
					});

					// duplicate check
					const duplicateInfo = duplicateLookup[rowIndex];
					let isDuplicate = false;

					if (duplicateInfo) {
						isDuplicate = true;
						fieldErrors['duplicate'] = duplicateInfo.message;
					}

					return {
						...row,
						fieldErrors,
						isDuplicate,   // <-- highlight flag for UI
						isValid: Object.keys(fieldErrors).length === 0,
						rowNumber: rowIndex + 1 // for table
					};
				});

				const allRowsValid = validatedRows.every(row => row.isValid);

				setValidatedData(validatedRows);
				await setIsFileValid(allRowsValid);

				if (allRowsValid && isCreate) {
					const formData = new FormData();
					formData.append("file", file);

					axios.post(`/api/v1/unofficial-address/upload`,
						formData,
						{
							headers: {
								"Authorization": `Bearer ${token}`,
								'Content-Type': 'multipart/form-data'
							}
						}
					)
						.then(res => {
							const data = res.data;
							// success case 
							if (data.invalidRowCount === 0) {
								setValidatedMessage(data.specificAddressSuccess);
								setTableData({
									header: result.meta.fields,
									data: validatedRows
								});
								setIsFileValid(true);
							}
							//duplicate or validation cases
							if (data.invalidRows && data.invalidRows.length > 0) {
								const updatedRows = [...validatedRows];  // from earlier

								data.invalidRows.forEach(invalid => {
									const rowIndex = invalid.rowNumber - 1;  // Convert 1-based → JS 0-based

									if (!updatedRows[rowIndex]) return;

									const errorMessage = invalid.errors?.errorMsg || "Invalid row";

									// Add backend error
									updatedRows[rowIndex].fieldErrors.backend = errorMessage;
									updatedRows[rowIndex].isDuplicate = false; // backend errors override duplicate highlight
									updatedRows[rowIndex].isValid = false;
								});

								// Update UI table with merged errors
								setTableData({
									header: result.meta.fields,
									data: updatedRows
								});

								setValidatedMessage("Validation failed. Please correct the highlighted rows.");
								setIsFileValid(false);
								return;
							}
							//access and specific address if they are already created and having delete flag false
							if (data.invalidRowCount === 0 && data.invalidRows.length === 0 && data.validRowCount === 0 && data.validRows.length === 0) {
								setTableData({ header: result.meta.fields, data: validatedRows });
								setValidatedMessage(`${data.errorMsg} ${data.accessAddressMsg}`);
								setIsFileValid(false);
							}
						})
						.catch(err => {
							/*const data = mockdata;
							// success case 
							if(data.invalidRowCount === 0 ) {
								setValidatedMessage(data.accessAddressSuccess);
								setTableData({
									header: result.meta.fields,
									data: validatedRows
								});
								setIsFileValid(true);
							}
							//duplicate or validation cases
							if (data.invalidRows && data.invalidRows.length > 0) {
								const updatedRows = [...validatedRows];  // from earlier
	
								data.invalidRows.forEach(invalid => {
									const rowIndex = invalid.rowNumber - 1;  // Convert 1-based → JS 0-based
	
									if (!updatedRows[rowIndex]) return;
	
									const errorMessage = invalid.errors?.errorMsg || "Invalid row";
	
									// Add backend error
									updatedRows[rowIndex].fieldErrors.backend = errorMessage;
									updatedRows[rowIndex].isDuplicate = false; // backend errors override duplicate highlight
									updatedRows[rowIndex].isValid = false;
								});
	
								// Update UI table with merged errors
								setTableData({
									header: result.meta.fields,
									data: updatedRows
								});
	
								setValidatedMessage("Validation failed. Please correct the highlighted rows.");
								setIsFileValid(false);
								return;
							}
							//access and specific address if they are already created and having delete flag false
							if (data.invalidRowCount === 0 &&  data.invalidRows.length ===0 &&  data.validRowCount === 0 && data.validRows.length ===0) {
								setTableData({ header: result.meta.fields, data: validatedRows });
								setValidatedMessage(`${data.errorMsg} ${data.accessAddressMsg}`);
								setIsFileValid(false);
							}	*/
							const data = err.response?.data;
							//duplicate or validation cases
							if (data.invalidRows && data.invalidRows.length > 0) {
								const updatedRows = [...validatedRows];  // from earlier

								data.invalidRows.forEach(invalid => {
									console.log(invalid)
									const rowIndex = invalid.rowNumber - 1;  // Convert 1-based → JS 0-based

									if (!updatedRows[rowIndex]) return;

									const errorMessage = invalid.errors?.errorMsg || "Invalid row";

									// Add backend error
									updatedRows[rowIndex].fieldErrors.backend = errorMessage;
									updatedRows[rowIndex].isDuplicate = false; // backend errors override duplicate highlight
									updatedRows[rowIndex].isValid = false;
								});

								// Update UI table with merged errors
								setTableData({
									header: result.meta.fields,
									data: updatedRows
								});

								setValidatedMessage(`${data.errorMsg} ${data.accessAddressMsg}`);
								setIsFileValid(false);
								return;
							}
							//access and specific address if they are already created and having delete flag false
							if (data.invalidRowCount === 0 && data.invalidRows.length === 0 && data.validRowCount === 0 && data.validRows.length === 0) {
								setTableData({ header: result.meta.fields, data: validatedRows });
								setValidatedMessage(`${data.errorMsg} ${data.accessAddressMsg}`);
							}
							else {
								setValidatedMessage(`${data?.errorMsg} ${data?.accessAddressMsg}`);
								setTableData({ header: result.meta.fields, data: validatedRows });
							}
							setIsFileValid(false);
						});
				} else {
					setValidatedMessage(
						allRowsValid
							? "Validated successfully. Please proceed with creation."
							: 'Uploaded file contains errors. Please refer to the table for fields with issues.'
					);
					setTableData({ header: result.meta.fields, data: validatedRows });
				}
			},
			header: true,
		});
	};


	// Handle the CSV download with address fields and a single error column at the end
	const handleDownload = () => {
		// Ensure there is data to download
		if (validatedData.length === 0) return [];

		// Map through the validated rows and extract only the address fields + error messages
		const dataToDownload = validatedData.map((row) => {
			const {
				streetName,
				buildingNo,
				buildingSuffix,
				floorId,
				suiteId,
				region,
				districtName,
				municipalityName,
				streetCode,
				postCode,
				coordinates,
				fieldErrors,
				backendError,
			} = row;

			// Collect all error messages (both field and backend errors)
			let errorMessages = [];

			// Add validation errors from the fieldErrors object
			Object.entries(fieldErrors).forEach(([field, errorMsg]) => {
				if (errorMsg) {
					errorMessages.push(`${field}: ${errorMsg}`);
				}
			});

			// Add the backend error if it exists
			if (backendError) {
				errorMessages.push(`Backend Error: ${backendError}`);
			}

			// Combine all errors into a single string for the last column
			const validationMessage = errorMessages.join(", ");

			// Prepare a row object that includes only the relevant address fields and the combined error message at the end
			return {
				streetName,
				buildingNo,
				buildingSuffix,
				floorId,
				suiteId,
				region,
				districtName,
				municipalityName,
				streetCode,
				postCode,
				coordinates,
				validationMessage, // Single error message column at the end
			};
		});

		return dataToDownload;
	};

	const handleSampleCSVDownload = () => {

		const headers = ['streetName', 'buildingNo', 'buildingSuffix', 'floorId', 'suiteId', 'postCode',
			'districtName', 'region', 'municipalityName', 'streetCode', 'coordinates']
		const csvContent = headers.join(',') + '\n'; // Add newline to end row

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);

		link.setAttribute('href', url);
		const now = new Date();
		const readableTimestamp = now.toISOString().split('.')[0].replace(/:/g, '-').replace('T', '_');

		// Set filename with timestamp
		const filename = `sample_${readableTimestamp}.csv`;
		link.setAttribute('download', filename);
		link.style.display = 'none';

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url); // Clean up
	};

	//Handle bulk address creation type.
	const handleType = (e) => {
		setFile(null);
		setValidatedData([]);
		setIsFileValid(false);
		setErrorMessage('');
		setAccessType(e.target.value);
		setValidatedMessage('');
		setTableData({ header: [], data: [] });
		setTextAreaContent({
			header: [],
			data: [],
			errors: []
		});
		setCsvInput('')
	}

	const handleValidate = () => {
		if (accessType === 'attachment') {
			handleCSVValidate()
		} else {
			handleValidateTextAreaData()
		}
	}

	const handleCreateAddresses = () => {

		if (accessType === 'attachment') {
			setErrorMessage('');
			setValidatedMessage('');
			handleCSVValidate(true);
		} else {
			handleValidateTextAreaData(true)
		}
	}

	// Handle Reset button
	const handleReset = useCallback(() => {
		setErrorMessage('');
		setFile(null);
		setValidatedData([]);
		setAccessType('attachment');
		inputFileRef.current.value = null;
		setIsFileValid(false);
		setValidatedMessage('');
		setTableData({ header: [], data: [] });
		setTextAreaContent({
			header: [],
			data: [],
			errors: []
		});
		setCsvInput('')
	}, [setTableData, setTextAreaContent]);

	//start of textarea input addresses creation functionality

	// Validation rules
	const validators = {
		'Post Code': (value) => (!value ? true : /^[A-Za-z0-9\s-]{3,10}$/.test(value) || 'Invalid Post Code'),
		'Coordinates': (value) =>
			(!value ? true : /^-?\d+(\.\d+)?\s* \s*-?\d+(\.\d+)?$/.test(value) || 'Invalid Coordinates')
	};

	const handleValidateTextAreaData = (isCreate = false) => {
		const rows = csvInput.trim().split('\n');
		if (rows.length < 2) {
			setValidatedMessage("Please enter addresses with a header and at least one row.");
			setIsFileValid(false)
			return;
		}

		const parsedHeaders = rows[0].split(',').map(h => h.trim());
		const parsedData = rows.slice(1).map(row => row.split(',').map(cell => cell.trim()));
		const rowErrors = [];

		const validatedTextAreaData = parsedData.map((row, rowIndex) => {
			const errorRow = {};
			const validatedRow = parsedHeaders.map((header, colIndex) => {
				const value = row[colIndex] || '';
				const isMandatory = mandatoryFields.includes(header);

				// Check mandatory
				if (isMandatory && !value) {
					errorRow[header] = 'This field is required';
				}

				// Apply format validation
				if (validators[header]) {
					const result = validators[header](value);
					if (result !== true && !errorRow[header]) {
						errorRow[header] = result;
					}
				}
				return value;
			});

			rowErrors.push(errorRow);
			return validatedRow;
		});

		setErrors(rowErrors);
		setValidatedMessage(
			rowErrors.length > 0
				? (!isCreate ? 'All records are validated. Please proceed with submission.' : 'Successfully created addresses')
				: 'Uploaded file contains errors. Please refer to the table for fields with issues.'
		);
		setTextAreaContent({ data: validatedTextAreaData, header: parsedHeaders, errors: rowErrors });
	};

	useEffect(() => {
		if (tabName === 'bulk-address') {
			handleReset()
		}
	}, [tabName, handleReset]);

	return (
		<div>
			<div className='form-group col-md-12'>
				<div className="csv-upload-container">
					<h6 className="text-center" style={{ marginTop: "16px", marginBottom: "16px" }}>
						Bulk addresses creation using csv upload and text
					</h6>
					<div className="form-group col-md-3">
						<label htmlFor="access-type">Access type</label>
						<select id="access-type" className="form-control form-control-sm"
							value={accessType} onChange={handleType} >
							<option value="attachment" >Attachment</option>
							<option value="text" >Text</option>
						</select>
					</div>
					{accessType === "attachment" &&
						<div className=''>
							<p style={{ marginLeft: '16px', marginBottom: '16px' }}>Click <button type="button" className="btn btn-link p-0 align-baseline" onClick={handleSampleCSVDownload}>here</button> to get sample CSV file format</p>
							{/* File upload input field*/}
							<div style={{ display: "flex", alignItems: "center" }}>
								<input
									style={{ maxWidth: "300px" }}
									type="file"
									accept=".csv"
									onChange={handleFileChange}
									ref={inputFileRef}
								/>

								{file && (
									<span style={{ marginLeft: "8px", display: "flex", alignItems: "center" }}>
										<span
											onClick={clearFile}
											style={{
												cursor: "pointer",
												color: "red",
												fontWeight: "bold",
												marginLeft: "6px"
											}}
											title="Remove file"
										>
											❌
										</span>
									</span>
								)}
							</div>


							{/* Error message */}
							{errorMessage && (
								<div style={{ color: 'red', marginTop: '10px' }}>
									{errorMessage}
								</div>
							)}
							{/* CSV download button */}
							{validatedData.length > 0 && (
								<CSVLink
									data={handleDownload()}
									filename="validated-data.csv"
									className="btn-download"
								>
									Download Validated CSV
								</CSVLink>
							)}
						</div>
					}{/* End of CSV file upload code changes */}
					{accessType === "text" &&
						<>
							<textarea rows="10" cols="100" placeholder="Street Name, Building Id, Building Suffix, Floor Id, Suite Id, Post Code, Region, Municipality Name, Street Code, Coordinates"
								value={csvInput}
								onChange={(e) => setCsvInput(e.target.value)} ></textarea>

							<br />
						</>
					}
				</div>
			</div>
			{validatedMessage && <div style={{ textAlign: 'center', marginBottom: '15px', color: isFileValid || errors.length > 0 ? 'green' : 'red' }}>{validatedMessage}</div>}
			<div className="d-flex flex-wrap justify-content-center " style={{ gap: "1rem", overflowX: "auto" }}>
				<button className="btn btn-primary" onClick={() => handleValidate()}>Validate</button>
				<button className="btn btn-primary" onClick={handleCreateAddresses}>Create</button>
				<button className="btn btn-secondary" onClick={handleReset}>{t('search.reset')}</button>
			</div>
		</div>
	)
}